import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { OrderInvoiceLogoImg } from "../lib/images";

interface OrderPDFProps {
  headers: string[];
  rows: any[][];
  exportInfo: {
    totalOrders: number;
    dateRange: string;
    statusName: string;
    totals: {
      serviceFeeTotal: number;
      totalAmount: number;
      deliveryFeeTotal: number;
      netTotal: number;
    };
    userRole: string;
  };
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    padding: 30,
    fontSize: 10,
    color: "#333",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  logo: {
    width: 150,
  },
  titleContainer: {
    alignItems: "flex-end",
    gap: 5,
  },
  title: {
    fontSize: 18,
    fontWeight: "extrabold",
    color: "#008000",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
  },
  infoSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontWeight: "bold",
    fontSize: 11,
    color: "#008000",
    marginBottom: 5,
  },
  summaryInfo: {
    marginBottom: 10,
    color: "#666",
  },
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1px solid #E4E4E4",
    paddingVertical: 10,
    backgroundColor: "#008000",
    color: "#fff",
    fontWeight: "bold",
    fontSize: 10,
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottom: "1px solid #E4E4E4",
  },
  cell: {
    width: "15%",
    textAlign: "center",
    color: "#fff", // For header cells
  },
  rowCell: {
    width: "15%",
    textAlign: "center",
    color: "#333", // For regular row cells
  },
  summarySection: {
    marginTop: 10,
    border: "1px solid #E4E4E4",
    padding: 10,
    borderRadius: 3,
    backgroundColor: "#F5F5F5",
    width: "40%",
    alignSelf: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  summaryText: {
    fontSize: 10,
  },
  footer: {
    marginTop: 20,
    borderTop: "1px solid #008000",
    paddingTop: 10,
    fontSize: 8,
    color: "#666",
    textAlign: "center",
  },
});

const OrderPDFDocument: React.FC<OrderPDFProps> = ({
  headers,
  rows,
  exportInfo,
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Image src={OrderInvoiceLogoImg} style={styles.logo} />
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Orders Report List</Text>
          <Text style={styles.subtitle}>
            Generated: {new Date().toLocaleDateString()}
          </Text>
          <Text style={styles.subtitle}>Status: {exportInfo.statusName}</Text>
        </View>
      </View>

      {/* Summary Information */}
      <View style={styles.infoSection}>
        <Text style={styles.sectionTitle}>Report Summary</Text>
        <Text style={styles.summaryInfo}>
          Total Orders: {exportInfo.totalOrders}
        </Text>
      </View>

      {/* Table */}
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          {headers.map((header, i) => (
            <Text key={i} style={styles.cell}>
              {header}
            </Text>
          ))}
        </View>
        {rows.map((row, i) => (
          <View key={i} style={styles.tableRow}>
            {row.map((cell, j) => (
              <Text key={j} style={styles.rowCell}>
                {cell}
              </Text>
            ))}
          </View>
        ))}
      </View>

      {/* Summary Section with net total */}
      <View style={styles.summarySection} wrap={false}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryText}>Total Orders:</Text>
          <Text style={styles.summaryText}>{exportInfo.totalOrders}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryText}>Total Amount:</Text>
          <Text style={styles.summaryText}>
            ${exportInfo.totals.totalAmount.toFixed(2)}
          </Text>
        </View>
        <View
          style={[
            styles.summaryRow,
            { marginTop: 5, paddingTop: 5, borderTop: "1px solid #E4E4E4" },
          ]}
        >
          <Text
            style={[
              styles.summaryText,
              { fontWeight: "bold", color: "#008000" },
            ]}
          >
            Net Total:
          </Text>
          <Text
            style={[
              styles.summaryText,
              { fontWeight: "bold", color: "#008000" },
            ]}
          >
            ${exportInfo.totals.netTotal.toFixed(2)}
          </Text>
        </View>
      </View>

      {/* Footer */}
      <Text style={styles.footer}>
        This is an automatically generated report. For any queries, please
        contact support.
      </Text>
    </Page>
  </Document>
);

export default OrderPDFDocument;
